import { createInertiaApp, ResolvedComponent, router } from "@inertiajs/react";
import { createElement } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { ToastProvider } from "../components/ui/toast";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { posthogState } from "../lib/posthogState";

// Initialize Sentry only in production
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://139c2d48582e898e9ce13c8b9ce18941@o4509370430980096.ingest.us.sentry.io/4509370487013376",
    sendDefaultPii: true,
  });
}

// PostHog types for shared props
interface PostHogConfig {
  api_key: string;
  host: string;
}

interface SharedProps {
  posthog?: PostHogConfig;
}

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  progress: {
    color: "#7626ff",
  },

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      ["../pages/**/*.tsx", "!../pages/**/*.test.tsx"],
      {
        eager: true,
      },
    );
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page) => createElement(Layout, null, page);

    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      // Initialize PostHog if config is available
      const sharedProps = props.initialPage.props as SharedProps;
      if (sharedProps.posthog?.api_key) {
        posthog.init(sharedProps.posthog.api_key, {
          api_host: sharedProps.posthog.host,
          capture_pageview: false,
          capture_pageleave: true,
        });
        posthogState.loaded = true;

        router.on("navigate", () => {
          posthog.capture("$pageview");
        });
      }

      const AppWithToast = () =>
        createElement(ToastProvider, null, createElement(App, props));

      // Use createRoot in development, hydrateRoot in production
      if (import.meta.env.DEV) {
        createRoot(el).render(createElement(AppWithToast));
      } else {
        hydrateRoot(el, createElement(AppWithToast));
      }
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
});
